import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { useFindOne, useSession, useGlobalAction } from '@gadgetinc/react';
import { api } from '../api';
import VoiceScenarioRunner from '../components/VoiceScenarioRunner';
import { generateDeviceFingerprint } from '../lib/deviceFingerprint';
import {
  Box,
  Container,
  Heading,
  Text,
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  Spinner,
  Alert,
  AlertIcon,
  AlertTitle,
  AlertDescription,
  Button,
  VStack
} from '@chakra-ui/react';

export default function VoiceScenarioPage() {
  const { scenarioId } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const voiceSimSessionId = searchParams.get('voiceSimSessionId');
  const voiceSimToken = searchParams.get('token');
  const isTokenizedAccess = !!voiceSimSessionId;

  // State for device fingerprint and validation
  const [deviceFingerprint, setDeviceFingerprint] = useState(null);
  const [validationResult, setValidationResult] = useState(null);
  const [validationError, setValidationError] = useState(null);

  // Fetch current session for logged-in access validation
  const session = useSession(api);

  // Global action for token validation
  const [{ data: validationData, fetching: fetchingValidation, error: validationActionError }, validateToken] = useGlobalAction(api.validateVoiceSimToken);

  // DEBUG: Log the validation hook state
  useEffect(() => {
    console.log('🔍 Validation hook state:', {
      validationData,
      fetchingValidation,
      validationActionError,
      hasData: !!validationData,
      dataType: typeof validationData,
      dataKeys: validationData ? Object.keys(validationData) : null
    });
  }, [validationData, fetchingValidation, validationActionError]);

  // Generate device fingerprint on mount
  useEffect(() => {
    const initFingerprint = async () => {
      if (isTokenizedAccess) {
        try {
          // Always generate a fresh fingerprint using the helper function
          const fingerprint = await generateDeviceFingerprint();
          setDeviceFingerprint(fingerprint);
          console.log('Device fingerprint initialized for tokenized access');
        } catch (error) {
          console.error('Failed to generate device fingerprint:', error);
          // Set a fallback fingerprint to prevent blocking
          const fallbackFingerprint = 'fallback_' + Date.now() + '_' + Math.random().toString(36).substring(2);
          setDeviceFingerprint(fallbackFingerprint);
        }
      }
    };
    
    initFingerprint();
  }, [isTokenizedAccess]);

  // Call validateToken when we have the required params
  useEffect(() => {
    if (isTokenizedAccess && voiceSimSessionId && deviceFingerprint && !validationData && !fetchingValidation) {
      console.log('Calling validateToken with:', { voiceSimSessionId, deviceFingerprint, scenarioId });
      validateToken({
        voiceSimSessionId: voiceSimSessionId,
        deviceFingerprint: deviceFingerprint,
        scenarioId: scenarioId,
        token: voiceSimToken || undefined,
      });
    }
  }, [isTokenizedAccess, voiceSimSessionId, deviceFingerprint, scenarioId, voiceSimToken, validationData, fetchingValidation]);

  // Watch for validation data from the hook
  useEffect(() => {
    console.log('📥 Validation data changed:', validationData);
    if (validationData) {
      console.log('✅ Setting validation result:', validationData);
      console.log('   - valid:', validationData.valid);
      console.log('   - voiceSimSession:', validationData.voiceSimSession);
      console.log('   - error:', validationData.error);
      setValidationResult(validationData);
    }
  }, [validationData]);

  // Watch for validation errors from the hook
  useEffect(() => {
    if (validationActionError) {
      console.error('Validation action error:', validationActionError);
      setValidationError(validationActionError);
    }
  }, [validationActionError]);

  // Extract voiceSimSession from validation result
  // Gadget useGlobalAction may wrap result differently - check both paths
  const unwrappedResult = validationResult?.result || validationResult;
  const [{ data: fallbackVoiceSimSession, fetching: fetchingFallbackVoiceSimSession, error: fallbackVoiceSimSessionError }] = useFindOne(
    api.voiceSimSession,
    voiceSimSessionId || "",
    {
      select: {
        id: true,
        token: true,
        status: true,
        expiresAt: true,
        attemptsAllowed: true,
        attemptsUsed: true,
        isActive: true,
        studentEmail: true,
        metadata: true,
        scenarioId: true,
        sharedBy: {
          firstName: true,
          lastName: true,
        },
      },
      skip: !isTokenizedAccess || !voiceSimSessionId,
    }
  );

  const validatedVoiceSimSession = unwrappedResult?.voiceSimSession;
  const fallbackTokenMatches =
    fallbackVoiceSimSession &&
    (!voiceSimToken || fallbackVoiceSimSession.token === voiceSimToken);
  const voiceSimSession = validatedVoiceSimSession || (fallbackTokenMatches ? fallbackVoiceSimSession : null);
  const tokenScenario = isTokenizedAccess ? voiceSimSession?.scenario : null;
  const fetchingToken =
    fetchingValidation ||
    fetchingFallbackVoiceSimSession ||
    (isTokenizedAccess && !deviceFingerprint);
  const tokenError = validationError || validationActionError || fallbackVoiceSimSessionError;

  console.log('🔍 Checking validation result:', {
    validationResult,
    unwrappedResult,
    hasValidationResult: !!validationResult,
    isValid: unwrappedResult?.valid,
    hasVoiceSimSession: !!voiceSimSession,
    voiceSimSession
  });

  const [{ data: fetchedScenario, fetching: fetchingScenario, error: scenarioError }] = useFindOne(
    api.clinicalScenario,
    scenarioId,
    {
      select: {
        id: true,
        title: true,
        description: { markdown: true },
        levelOfCare: true,
        institution: true,
        isActive: true,
        createdBy: { id: true }
      },
      skip: isTokenizedAccess,
    }
  );
  const scenario = isTokenizedAccess ? (tokenScenario || fetchedScenario) : fetchedScenario;

  const handleComplete = () => {
    if (isTokenizedAccess) {
      // For tokenized access, just show a completion message
      // The instructor will see the results in their admin panel
    } else {
      // For logged-in users, navigate back to scenarios
      navigate('/my-scenarios');
    }
  };

  // Loading state
  const isLoading = (!isTokenizedAccess && fetchingScenario) || (isTokenizedAccess && fetchingToken);
  if (isLoading) {
    return (
      <Container maxW="container.lg" py={8}>
        <VStack spacing={4}>
          <Spinner size="xl" />
          <Text>
            {fetchingToken ? 'Validating access...' : 'Loading scenario...'}
          </Text>
        </VStack>
      </Container>
    );
  }

  // SECURITY: Block unauthenticated users without tokens (after hooks initialize)
  if (!session && !voiceSimSessionId) {
    return (
      <Container maxW="container.lg" py={8}>
        <Alert status="error">
          <AlertIcon />
          <Box>
            <AlertTitle>Invalid Access</AlertTitle>
            <AlertDescription>
              You must use the invitation link provided by your instructor to access this scenario.
              Direct URL access is not permitted for security and tracking purposes.
            </AlertDescription>
          </Box>
        </Alert>
      </Container>
    );
  }

  // SECURITY: Block if tokenized access but validation hasn't completed yet
  if (isTokenizedAccess && !validationResult && !voiceSimSession) {
    return (
      <Container maxW="container.lg" py={8}>
        <VStack spacing={4}>
          <Spinner size="xl" />
          <Text>Validating access credentials...</Text>
        </VStack>
      </Container>
    );
  }

  // Access validation for tokenized access
  if (isTokenizedAccess) {
    // Check for device mismatch first
    if (unwrappedResult && !unwrappedResult.valid && unwrappedResult.deviceMismatch) {
      const canRestart = unwrappedResult.attemptsRemaining > 1;
      
      return (
        <Container maxW="container.lg" py={8}>
          <VStack spacing={4} align="stretch">
            <Alert status="error">
              <AlertIcon />
              <Box flex="1">
                <AlertTitle>Device Conflict Detected</AlertTitle>
                <AlertDescription>
                  An active session for this scenario is already running on a different device. 
                  This could happen if:
                  <VStack align="start" mt={2} spacing={1} fontSize="sm">
                    <Text>• You started on another device and didn't finish</Text>
                    <Text>• Your browser cleared its cache/data</Text>
                    <Text>• You're using a different browser on the same device</Text>
                  </VStack>
                </AlertDescription>
              </Box>
            </Alert>

            {canRestart ? (
              <>
                <Alert status="warning">
                  <AlertIcon />
                  <Box flex="1">
                    <AlertTitle>You have {unwrappedResult.attemptsRemaining} attempt(s) remaining</AlertTitle>
                    <AlertDescription>
                      You can restart from the beginning on this device, which will abandon the previous session 
                      and count as one attempt. Or you can return to your original device to continue.
                    </AlertDescription>
                  </Box>
                </Alert>

                <Button 
                  colorScheme="orange" 
                  size="lg"
                  onClick={async () => {
                    // Store fingerprint in localStorage for consistency across reloads
                    const currentFingerprint = deviceFingerprint;
                    if (currentFingerprint) {
                      localStorage.setItem('deviceFingerprint_v2', currentFingerprint);
                    }
                    
                    try {
                      // Pass deviceFingerprint to help endVoiceSession find the right session
                      await api.endVoiceSession({
                        voiceSimSessionId: unwrappedResult.voiceSimSessionId,
                        reason: 'device_switch',
                        completedSuccessfully: false,
                        deviceFingerprint: currentFingerprint
                      });
                      // Reload the page to restart
                      window.location.reload();
                    } catch (error) {
                      console.error('Failed to restart:', error);
                      alert('Failed to restart session. Please contact your instructor.');
                    }
                  }}
                >
                  🔄 Restart on This Device (Uses 1 Attempt)
                </Button>
              </>
            ) : (
              <Alert status="warning">
                <AlertIcon />
                <Box>
                  <AlertTitle>Cannot Restart - No Attempts Remaining</AlertTitle>
                  <AlertDescription>
                    You have no remaining attempts to restart on a new device. 
                    Please return to your original device to complete the scenario, or contact your instructor.
                  </AlertDescription>
                </Box>
              </Alert>
            )}

            <Alert status="info" fontSize="sm">
              <AlertIcon />
              <Box>
                <AlertTitle fontSize="md">Need Help?</AlertTitle>
                <AlertDescription>
                  If you believe this error is incorrect or need to switch devices for a valid reason, 
                  please contact your instructor at <strong>{unwrappedResult.voiceSimSession?.studentEmail || 'support'}</strong> or
                  email <strong>support@elevationems.com</strong> with your name and scenario details.
                </AlertDescription>
              </Box>
            </Alert>
          </VStack>
        </Container>
      );
    }

    // Check if validation failed
    if (unwrappedResult && !unwrappedResult.valid) {
      return (
        <Container maxW="container.lg" py={8}>
          <Alert status="error">
            <AlertIcon />
            <Box>
              <AlertTitle>Invalid Access</AlertTitle>
              <AlertDescription>
                {unwrappedResult.message || unwrappedResult.error || 'Unable to validate access. Please contact your instructor for a new invitation link.'}
              </AlertDescription>
            </Box>
          </Alert>
        </Container>
      );
    }

    // Check if voiceSimSession exists (after validation)
    if (tokenError || (unwrappedResult && unwrappedResult.valid && !voiceSimSession)) {
      return (
        <Container maxW="container.lg" py={8}>
          <Alert status="error">
            <AlertIcon />
            <Box>
              <AlertTitle>Invalid Access Token</AlertTitle>
              <AlertDescription>
                The access token provided is invalid or has been revoked. Please contact your instructor for a new invitation link.
              </AlertDescription>
            </Box>
          </Alert>
        </Container>
      );
    }

    // Check if voiceSimSession is active
    if (voiceSimSession && !voiceSimSession.isActive) {
      return (
        <Container maxW="container.lg" py={8}>
          <Alert status="error">
            <AlertIcon />
            <Box>
              <AlertTitle>Access Token Inactive</AlertTitle>
              <AlertDescription>
                This access token has been deactivated. Please contact your instructor for assistance.
              </AlertDescription>
            </Box>
          </Alert>
        </Container>
      );
    }

    // Check if voiceSimSession is expired
    if (voiceSimSession?.expiresAt) {
      const now = new Date();
      const expiresAt = new Date(voiceSimSession.expiresAt);
      if (expiresAt < now) {
        return (
          <Container maxW="container.lg" py={8}>
            <Alert status="error">
              <AlertIcon />
              <Box>
                <AlertTitle>Access Token Expired</AlertTitle>
                <AlertDescription>
                  Your access to this scenario expired on {expiresAt.toLocaleDateString()}. Please contact your instructor for a new invitation link.
                </AlertDescription>
              </Box>
            </Alert>
          </Container>
        );
      }
    }

    // Check if attempts remaining
    if (voiceSimSession && voiceSimSession.attemptsUsed >= voiceSimSession.attemptsAllowed) {
      return (
        <Container maxW="container.lg" py={8}>
          <Alert status="warning">
            <AlertIcon />
            <Box>
              <AlertTitle>No Attempts Remaining</AlertTitle>
              <AlertDescription>
                You have used all {voiceSimSession.attemptsAllowed} allowed {voiceSimSession.attemptsAllowed === 1 ? 'attempt' : 'attempts'} for this scenario. 
                Please contact your instructor if you need additional attempts.
              </AlertDescription>
            </Box>
          </Alert>
        </Container>
      );
    }

    // Check if scenario ID matches
    if (voiceSimSession) {
      const sessionScenarioId = voiceSimSession.scenarioId || (
        typeof voiceSimSession.scenario === 'string'
          ? voiceSimSession.scenario
          : voiceSimSession.scenario?.id
      );

      if (sessionScenarioId !== scenarioId) {
        return (
          <Container maxW="container.lg" py={8}>
            <Alert status="error">
              <AlertIcon />
              <Box>
                <AlertTitle>Scenario Mismatch</AlertTitle>
                <AlertDescription>
                  The access token provided is not valid for this scenario. Please use the correct invitation link provided by your instructor.
                </AlertDescription>
              </Box>
            </Alert>
          </Container>
        );
      }
    }
  } else {
    // Non-tokenized access for direct testing:
    // allow instructor/admin/system-admin; students still require tokenized links.
    if (!session) {
      return (
        <Container maxW="container.lg" py={8}>
          <Alert status="error">
            <AlertIcon />
            <Box>
              <AlertTitle>Authentication Required</AlertTitle>
              <AlertDescription>
                You must use the invitation link provided by your instructor to access this scenario.
                Direct URL access requires system administrator privileges.
              </AlertDescription>
            </Box>
          </Alert>
          <Button mt={4} onClick={() => navigate('/sign-in')}>Sign In</Button>
        </Container>
      );
    } else {
      const userRoles = Array.isArray(session?.user?.roles) ? session.user.roles : [];
      const hasDirectTestAccess = userRoles.some(role => {
        const key = typeof role === "string" ? role : role?.key || role?.name;
        return key === "system-admin" || key === "admin" || key === "instructor";
      });
      
      if (!hasDirectTestAccess) {
        return (
          <Container maxW="container.lg" py={8}>
            <Alert status="error">
              <AlertIcon />
              <Box>
                <AlertTitle>Token Required</AlertTitle>
                <AlertDescription>
                  Voice scenario access requires an invitation token for this account type.
                  Please use the link provided by your instructor.
                </AlertDescription>
              </Box>
            </Alert>
            <Button mt={4} onClick={() => navigate('/my-scenarios')}>Back to My Scenarios</Button>
          </Container>
        );
      }

      // Instructor/admin/system-admin direct test access granted
      console.warn('Direct test access (no token) for scenario:', scenarioId, userRoles);
      const isSystemAdmin = userRoles.some(role =>
        role === 'system-admin' || role?.key === 'system-admin' || role?.name === 'system-admin'
      );
      if (isSystemAdmin) {
        console.warn('System admin bypassing token validation for scenario:', scenarioId);
      }
    }
  }

  // Scenario validation (applies to both access types)
  if ((!isTokenizedAccess && scenarioError) || !scenario) {
    return (
      <Container maxW="container.lg" py={8}>
        <Alert status="error">
          <AlertIcon />
          <Box>
            <AlertTitle>Error loading scenario</AlertTitle>
            <AlertDescription>
              {scenarioError?.message || 'Scenario not found'}
            </AlertDescription>
          </Box>
        </Alert>
        {!isTokenizedAccess && (
          <Button mt={4} onClick={() => navigate('/my-scenarios')}>Back to Scenarios</Button>
        )}
      </Container>
    );
  }

  if (!scenario.isActive) {
    return (
      <Container maxW="container.lg" py={8}>
        <Alert status="warning">
          <AlertIcon />
          <Box>
            <AlertTitle>Scenario Unavailable</AlertTitle>
            <AlertDescription>
              This scenario is currently inactive.
            </AlertDescription>
          </Box>
        </Alert>
        {!isTokenizedAccess && (
          <Button mt={4} onClick={() => navigate('/my-scenarios')}>Back to Scenarios</Button>
        )}
      </Container>
    );
  }

  return (
    <Box bg="gray.900" minH="100vh">
      <Container maxW="container.xl" py={6}>
        {/* Breadcrumb */}
        {!isTokenizedAccess && (
          <Breadcrumb mb={4} color="gray.400" fontSize="sm">
            <BreadcrumbItem>
              <BreadcrumbLink onClick={() => navigate('/my-scenarios')}>
                Scenarios
              </BreadcrumbLink>
            </BreadcrumbItem>
            <BreadcrumbItem>
              <BreadcrumbLink onClick={() => navigate('/my-scenarios')}>
                Voice
              </BreadcrumbLink>
            </BreadcrumbItem>
            <BreadcrumbItem isCurrentPage>
              <Text>Voice Scenario</Text>
            </BreadcrumbItem>
          </Breadcrumb>
        )}
        
        {/* Voice Scenario Runner */}
        <VoiceScenarioRunner 
          scenarioId={scenarioId}
          voiceSimSession={voiceSimSession}
          onComplete={handleComplete}
        />
        
        {/* Back button */}
        {!isTokenizedAccess && (
          <Box mt={6}>
            <Button 
              variant="outline" 
              onClick={() => navigate('/my-scenarios')}
            >
              ← Back to Scenarios
            </Button>
          </Box>
        )}
      </Container>
    </Box>
  );
}
